import React, { useRef, useEffect, useState, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import { useGLTF } from "@react-three/drei";
import { Vector3 } from "three";

const INITIAL_ROTATION = { x: -0.05, y: 0.2, z: 0.1 };

export default function HeroZoomModel({ scrollY, isMobile }) {
  const groupRef = useRef();
  const capRef = useRef();
  const screwRef = useRef();
  const { scene } = useGLTF("/3dModel/injection_model_cap_and_screw.glb");

  const initialPosition = useMemo(
    () => (isMobile ? { x: 0.2, y: 0, z: 0 } : { x: 1, y: 0, z: 0 }),
    [isMobile]
  );
  const initialScale = useMemo(() => (isMobile ? 1 : 0.5), [isMobile]);

  const originalCapPos = useRef(new Vector3());
  const originalScrewPos = useRef(new Vector3());
  const currentCapScale = useRef(1);

  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);
  const dragDeltaX = useRef(0);

  useEffect(() => {
    scene.traverse((child) => {
      if (child.name.toLowerCase().includes("cap")) capRef.current = child;
      if (child.name.toLowerCase().includes("screw")) screwRef.current = child;
    });

    if (capRef.current) originalCapPos.current.copy(capRef.current.position);
    if (screwRef.current)
      originalScrewPos.current.copy(screwRef.current.position);

    if (groupRef.current) {
      groupRef.current.position.set(
        initialPosition.x,
        initialPosition.y,
        initialPosition.z
      );
      groupRef.current.rotation.set(
        INITIAL_ROTATION.x,
        INITIAL_ROTATION.y,
        INITIAL_ROTATION.z
      );
      groupRef.current.scale.setScalar(initialScale);
    }

    const handleMouseDown = (e) => {
      !isDragging && setIsDragging(true);
      dragStartX.current = e.clientX;
    };
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      dragDeltaX.current = e.clientX - dragStartX.current;
    };
    const handleMouseUp = () => {
      setIsDragging(false);
      dragDeltaX.current = 0;
    };

    window.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [scene, isDragging, initialScale, initialPosition]);

  useFrame((state) => {
    if (!capRef.current || !screwRef.current || !groupRef.current) return;

    const heroHeight = window.innerHeight;
    const scrollPercent = Math.min(scrollY / heroHeight, 1);

    // Idle bump
    const amplitude =
      scrollPercent <= 0.1 ? 0.05 * (1 - scrollPercent / 0.1) : 0;
    const bump = Math.sin(state.clock.getElapsedTime() * 2) * amplitude;
    groupRef.current.position.y = initialPosition.y + bump;

    // Y-axis pan rotation (idle + drag)
    const maxYRotation = 0.1745;
    let yPan = 0;
    if (scrollPercent <= 0.1) {
      const idlePan =
        Math.sin(state.clock.getElapsedTime() * 1.5) *
        maxYRotation *
        (1 - scrollPercent / 0.1);
      const dragPan =
        (dragDeltaX.current / window.innerWidth) * maxYRotation * 2;
      yPan = idlePan + dragPan;
    }
    groupRef.current.rotation.set(
      INITIAL_ROTATION.x,
      INITIAL_ROTATION.y + yPan,
      INITIAL_ROTATION.z
    );

    // Separation & zoom
    let capTargetPos = originalCapPos.current.clone();
    let screwTargetPos = originalScrewPos.current.clone();
    let capTargetScale = 1;
    let screwTargetScale = 1;

    const separationStart = 0.05;
    const separationEnd = 0.5;
    const zoomStart = isMobile ? 0.6 : 0.4; // start zoom earlier on mobile
    const zoomEnd = isMobile ? 0.7 : 0.7; // reach full zoom faster on mobile

    if (scrollPercent > separationStart) {
      const progress = Math.min(
        (scrollPercent - separationStart) / (separationEnd - separationStart),
        1
      );
      capTargetPos.x -= 1.5 * progress;
      screwTargetPos.x += 1.5 * progress;
    }

    if (scrollPercent > zoomStart) {
      const zoomProgress = Math.min(
        (scrollPercent - zoomStart) / (zoomEnd - zoomStart),
        1
      );

      capTargetScale = isMobile
        ? 1 + zoomProgress * 20
        : 1 + (zoomProgress * 17) / initialScale;
    }

    if (scrollPercent > separationEnd) {
      const extraProgress =
        (scrollPercent - separationEnd) / (1 - separationEnd);
      capTargetPos.z = originalCapPos.current.z - extraProgress * 5;
      screwTargetScale = 1 - extraProgress;
      screwTargetPos.x += extraProgress * 5;
    }

    const lerpSpeed = 0.1;
    currentCapScale.current +=
      (capTargetScale - currentCapScale.current) * lerpSpeed;

    capRef.current.position.copy(capTargetPos);
    screwRef.current.position.copy(screwTargetPos);
    capRef.current.scale.setScalar(currentCapScale.current);
    screwRef.current.scale.setScalar(screwTargetScale);
  });

  return <primitive ref={groupRef} object={scene} />;
}
